/**
 * Single source of the LinkedIn URL rule mirrored from the API's
 * `App\Rules\V2\LinkedinUrl`: the host must be `linkedin.com` or a subdomain.
 * The server additionally applies Laravel's `url` rule, so submitted values
 * must carry an http(s) scheme.
 */
export function isLinkedInHost(hostname: string): boolean {
  const host = hostname.toLowerCase();
  return host === 'linkedin.com' || host.endsWith('.linkedin.com');
}

/** Strict check for an already-complete URL (admin edit mirrors the API rule). */
export function isLinkedInUrl(value: string): boolean {
  try {
    const url = new URL(value);
    if (!['http:', 'https:'].includes(url.protocol)) return false;
    return isLinkedInHost(url.hostname);
  } catch {
    return false;
  }
}

/**
 * Lenient variant for the public claim form: tolerates a missing scheme
 * (`www.linkedin.com/in/jane`) and returns the normalized submit-ready URL,
 * or null when the value can't be a valid LinkedIn URL.
 */
export function normalizeLinkedInUrl(value: string): string | null {
  const trimmed = value.trim();
  if (!trimmed) return null;

  const candidate = /^[a-z][a-z\d+\-.]*:\/\//i.test(trimmed)
    ? trimmed
    : `https://${trimmed}`;

  try {
    const url = new URL(candidate);
    if (!['http:', 'https:'].includes(url.protocol)) return null;
    if (!isLinkedInHost(url.hostname)) return null;
    return url.toString();
  } catch {
    return null;
  }
}
